import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone, Send } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = ["Web Development", "Mobile App Development", "Digital Marketing", "Something else"];

const initialForm = { name: "", email: "", company: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch within one working day.");
      setForm(initialForm);
    } catch {
      toast.error("Couldn't send your message. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-zinc-200 rounded-xl px-5 py-4 text-sm font-medium text-black placeholder:text-zinc-400 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-400/20 transition-all";

  const labelClass =
    "block font-mono-accent text-[11px] uppercase tracking-[0.2em] text-zinc-700 mb-3";

  const methods = [
    { icon: Mail, label: "Email us", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testId: "contact-email-link" },
    { icon: Phone, label: "Call us", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`, testId: "contact-phone-link" },
    { icon: MapPin, label: "Visit us", value: CONTACT.address, href: null, testId: "contact-address" },
  ];

  return (
    <main data-testid="contact-page" className="pt-20">
      <section className="relative py-14 lg:py-20 overflow-hidden grid-bg">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-cyan-400/10 blur-[140px] rounded-full" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <MaskedLine delay={0.1}>
            <Overline>Contact Us</Overline>
          </MaskedLine>
          <h1 className="font-display tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl mt-4">
            <MaskedLine delay={0.25}>Say the word.</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-stroke">We build it.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="pt-16 lg:pt-24 pb-24 lg:pb-32 bg-white text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <h2 className="font-display tracking-tight leading-tight text-3xl sm:text-4xl">
              Let's build your next chapter.
            </h2>
            <p className="text-zinc-600 text-sm sm:text-base leading-relaxed mt-5 max-w-md">
              Tell us where you want to go — we'll reply with honest advice, a clear
              plan and a fixed quote. No jargon, no pressure.
            </p>

            <div className="space-y-4 mt-10">
              {methods.map((m) => {
                const Icon = m.icon;
                const inner = (
                  <>
                    <span className="w-11 h-11 rounded-xl bg-cyan-400/15 grid place-items-center shrink-0">
                      <Icon className="w-5 h-5 text-cyan-600" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono-accent text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {m.label}
                      </span>
                      <span className="block font-semibold text-sm mt-1 group-hover:text-cyan-600 transition-colors">
                        {m.value}
                      </span>
                    </span>
                  </>
                );
                const cls =
                  "group flex items-center gap-4 border border-zinc-200 rounded-2xl p-5 bg-white hover:border-cyan-500 transition-colors";
                return m.href ? (
                  <a key={m.label} href={m.href} data-testid={m.testId} className={cls}>
                    {inner}
                  </a>
                ) : (
                  <div key={m.label} data-testid={m.testId} className={cls}>
                    {inner}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              {CONTACT.socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.label.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-4 py-2.5 text-sm font-medium text-zinc-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors"
                >
                  {s.label}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-6 sm:p-10">
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className={labelClass}>Your name *</label>
                    <input
                      id="contact-name"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Ada Lovelace"
                      data-testid="contact-form-name-input"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClass}>Email address *</label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="ada@company.com"
                      data-testid="contact-form-email-input"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-company" className={labelClass}>Company name</label>
                  <input
                    id="contact-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Company Ltd"
                    data-testid="contact-form-company-input"
                    className={inputClass}
                  />
                </div>
                <div>
                  <span className={labelClass}>What do you need?</span>
                  <div className="flex flex-wrap gap-2" data-testid="contact-form-subject-input">
                    {SERVICE_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm({ ...form, service: opt })}
                        data-testid={`contact-service-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                          form.service === opt
                            ? "bg-black text-white border-black"
                            : "bg-white text-zinc-600 border-zinc-200 hover:border-cyan-500"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className={labelClass}>Tell us about the project *</label>
                  <textarea
                    id="contact-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Goals, timelines, ambitions — the more the better."
                    data-testid="contact-form-message-input"
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-black text-white font-semibold text-sm px-8 py-4 hover:bg-cyan-400 hover:text-black transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send message"}
                  <Send className="w-4 h-4" />
                </button>
                <p className="text-center text-xs text-zinc-500">
                  We reply within one working day.
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
